import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import config from '../../config';
import {
  getDisplayDosage,
  formatFrequencyCompact,
  formatDoseAmount,
  isClinicalStudyDose,
} from '../../utils/dosageDisplay';
import { color, r, radius, tabularNums } from '../../theme/tokens';

// DoseChip — dose, unit and frequency as ONE chip: "2 billion CFU · once/day".
//
// One chip, one separator. The surface this replaced stacked the amount, the
// unit and the frequency as three separate typographic elements in a right-hand
// column, which read as three facts about three different things.
//
// In edit mode the chip is the dose editor (ruled 2026-08-12, STACK-480):
// tapping it swaps the chip for an in-flow amount + unit editor. That replaces
// the three-dot menu and its modal dialog, which the handoff removes without
// drawing anything in their place — this fills the gap rather than dropping the
// ability to change a dose. It edits in flow rather than in a popover because
// the repo bans absolute positioning, and an editor that reflows the row is
// also the one that survives a 390px viewport at 200% text.

/**
 * The chip's text, or null when there is no dose to show.
 *
 * The frequency half is gated on `pdf_verified` exactly as the surface it
 * replaces was: only PDF-extracted doses carry a trustworthy `frequency`, and
 * showing one derived from anything else would overstate what we know.
 */
export const doseChipText = (supplement) => {
  if (!supplement) return null;
  const raw = getDisplayDosage(supplement);
  if (raw === null || raw === undefined || String(raw).trim() === '') return null;
  const amount = formatDoseAmount(raw);

  const unit = supplement.unit ? ` ${supplement.unit}` : '';
  const frequency =
    supplement.dosageSource === 'pdf_verified'
      ? formatFrequencyCompact(supplement.dosageDetails?.frequency)
      : null;

  return `${amount}${unit}${frequency ? ` · ${frequency}` : ''}`;
};

/**
 * The clinical-study marker's tooltip, or null when the marker should not show.
 * Quotes the same rounded amount the chip prints, so the two can never disagree.
 */
export const clinicalDoseLabel = (supplement) => {
  if (!isClinicalStudyDose(supplement)) return null;
  const raw = getDisplayDosage(supplement);
  if (raw === null || raw === undefined || String(raw).trim() === '') return null;
  const unit = supplement.unit ? ` ${supplement.unit}` : '';
  return `Clinical study dose: ${formatDoseAmount(raw)}${unit}`;
};

// Exported for `analyze/SplitDoseChip`, which must match this chip's border,
// radius, type and height exactly so an analysed row reads like any other.
export const chipSx = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: `${r(6)} ${r(11)}`,
  border: `${r(1)} solid ${color.line}`,
  borderRadius: radius.sm,
  backgroundColor: color.paper,
  color: color.ink,
  fontSize: r(12),
  fontWeight: 600,
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  ...tabularNums,
};

// The editor is its own component so that its fields are seeded by `useState`
// initialisers at mount. Seeding them from an effect after the fact meant the
// values landed a render late — and, depending on when React flushed passive
// effects, sometimes not at all. Mounting fresh per editing session also means
// an abandoned edit cannot leak into the next one.
const DoseEditor = ({ supplement, supplementName, onCommit, onCancel }) => {
  // Seeds from what the chip SHOWS — the rounded display amount — so the
  // editor and the chip can never disagree about the number being edited. For
  // pdf_verified rows getDisplayDosage prefers per_dose_amount, so seeding from
  // `supplement.dosage` would put a different number in front of the user than
  // the chip just displayed.
  const shown = getDisplayDosage(supplement);
  const [amount, setAmount] = useState(shown == null ? '' : String(formatDoseAmount(shown)));
  const [unit, setUnit] = useState(supplement?.unit ?? '');
  // Tracked so the wrapper's key handling can stand aside while the unit menu
  // is open — see the onKeyDown comment below.
  const [unitOpen, setUnitOpen] = useState(false);
  const amountRef = useRef(null);

  useEffect(() => {
    if (amountRef.current) amountRef.current.focus();
  }, []);

  const commit = () => onCommit({ dosage: amount, unit });

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: r(6),
        padding: `${r(4)} ${r(6)}`,
        border: `${r(1)} solid ${color.accent}`,
        borderRadius: radius.sm,
        backgroundColor: color.paper,
        flexWrap: 'wrap',
      }}
      onKeyDown={(e) => {
        // The unit Select renders its menu through a portal, and React bubbles
        // portal events along the COMPONENT tree rather than the DOM tree — so
        // a keypress inside the open menu still reaches this handler. Enter
        // there means "choose this unit" and Escape means "close the menu";
        // treating either as save/cancel makes the unit unreachable by
        // keyboard, which is the whole control for anyone not using a mouse.
        if (unitOpen) return;
        if (e.key === 'Enter' && e.target === amountRef.current) {
          e.preventDefault();
          commit();
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
    >
      <InputBase
        inputRef={amountRef}
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        type="number"
        inputProps={{ 'aria-label': `Daily dosage for ${supplementName}`, min: 0 }}
        sx={{
          width: r(64),
          fontSize: r(12),
          fontWeight: 600,
          color: color.ink,
          ...tabularNums,
          '& input': { padding: `${r(2)} ${r(4)}` },
        }}
      />
      <Select
        value={unit}
        onChange={(e) => setUnit(e.target.value)}
        open={unitOpen}
        onOpen={() => setUnitOpen(true)}
        onClose={() => setUnitOpen(false)}
        variant="standard"
        disableUnderline
        inputProps={{ 'aria-label': `Unit for ${supplementName}` }}
        sx={{
          fontSize: r(12),
          fontWeight: 600,
          color: color.ink,
          '& .MuiSelect-select': { padding: `${r(2)} ${r(4)}` },
        }}
      >
        {Object.entries(config.UNITS).map(([key, label]) => (
          <MenuItem key={key} value={label}>
            {label}
          </MenuItem>
        ))}
      </Select>
      <IconButton
        size="small"
        onClick={commit}
        aria-label={`Save dosage for ${supplementName}`}
        sx={{ color: color.accent, fontSize: r(13), padding: r(2) }}
      >
        ✓
      </IconButton>
      <IconButton
        size="small"
        onClick={onCancel}
        aria-label="Cancel dosage edit"
        sx={{ color: color.inkMuted, fontSize: r(13), padding: r(2) }}
      >
        ×
      </IconButton>
    </Box>
  );
};

const DoseChip = ({
  supplement,
  editing = false,
  onEditingChange,
  onChange,
  supplementName,
  icon: Icon,
}) => {
  const text = doseChipText(supplement);
  const studyLabel = clinicalDoseLabel(supplement);

  if (editing) {
    return (
      <DoseEditor
        supplement={supplement}
        supplementName={supplementName}
        onCommit={(next) => {
          onChange?.(next);
          onEditingChange?.(false);
        }}
        onCancel={() => onEditingChange?.(false)}
      />
    );
  }

  // One presentation for both modes now. The chip is display only: the row's
  // kebab menu owns "Edit dose", so there is no hidden tap target here and no
  // inline "Edit" text crowding the numbers, which are the point of the chip
  // (Tanu, 2026-08-14). A doseless supplement is still reachable — the kebab is
  // present on every editable row whether or not a dose exists.
  if (!text) return null;

  return (
    <Box component="span" sx={chipSx}>
      {Icon && (
        <Icon
          aria-hidden="true"
          sx={{
            fontSize: r(14),
            mr: r(5),
            flex: 'none',
            // The library's capsule is drawn lying down. Upright reads cleaner
            // at chip height; there is no vertical capsule in the library to
            // import instead. See the PR note.
            transform: 'rotate(90deg)',
          }}
        />
      )}
      {text}
      {/* Provenance rides inside the chip so dose, frequency and source read as
          one fact rather than three loose elements. */}
      {studyLabel && (
        <Tooltip title={studyLabel} arrow placement="top">
          <InfoOutlinedIcon
            aria-label={studyLabel}
            role="img"
            sx={{ fontSize: r(12), ml: r(5), flex: 'none', color: color.inkMuted }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

export default DoseChip;
